import { RequestAnalysis, RequestAnalysisData } from "../../../models/intake";
import React, { useEffect, useRef, useState } from "react";
import { Toast } from "react-bootstrap";
import { RootState } from "typesafe-actions";
import { connect } from "react-redux";
import { PartialDateRange } from "../../../models/misc";
import "../IntakeResultsDialog.scss";
import InfoFilledSmall from "../../../assets/images/Info_icon.png";
import CancelFilledSmall from "../../../assets/images/cancel_filled_small.svg";
import UndoFilledSmall from "../../../assets/images/undo_filled_small.svg";
import CopyToClipboard from "../../../common/copyToClipboard/copyToClipboard";
import { Dispatch } from "redux";
import { IntakeFeedbackApiStatus } from "../DataEntryDetailsDucks";
import {
    IntakeFeedback,
    setFeedbackForIntakeFieldActionCreator
} from "../IntakeDucks";
import { API_FETCHING } from "../../../common/constants/ApiConstants";
import { coerceDate } from "./RecordsRequestedField";

export interface IntakeResultsModalProps {
    readonly requestAnalysis?: RequestAnalysis;
    readonly show?: boolean;
    readonly onClose?: () => void;
    readonly facilityId: number;
    readonly requestBufferId: number;
}

interface ReduxStateProps {
    readonly intakeFeedbackSubmitStatus: IntakeFeedbackApiStatus;
}

interface ReduxDispatchProps {
    readonly setFeedback: (feedback: IntakeFeedback) => void;
}

type Tab = "Details" | "Requestor" | "Billing";

type IntakeResultItem = {
    readonly name: string;
    readonly tab: Tab;
    readonly fieldName: keyof RequestAnalysisData;
    // Defaults to true. Set false for fields the backend has no feedback
    // target for (e.g. serviceDates, which is modeled as individual date-range
    // identifiers, not an aggregate). Submitting feedback for such a field
    // returns HTTP 400. See bug #32574.
    readonly feedbackSupported?: boolean;
}

/**
 * The declared Date type is a lie on a rehydrated store. redux-persist
 * (redux/index.tsx blacklists only loginState) JSON-round-trips dataEntryState,
 * which holds requestAnalysis - so every Date comes back as an ISO string.
 * The old shape tested `"getTime" in date`, and `in` throws a TypeError on a
 * string primitive; because this runs in the render body it took the whole
 * React root down rather than one row (AB#35422 review, thread 51808).
 *
 * A plain `instanceof Date` guard would stop the crash but render an empty
 * range instead of the dates, so this coerces - reusing RecordsRequestedField's
 * coerceDate, which exists for the identical rehydrate bug.
 */
export function formatDate(date?: Date | string | PartialDateRange | null): string {
    if (!date) {
        return "";
    } else if (typeof date === "string" || date instanceof Date) {
        const parsed = coerceDate(date);
        // Keeps the old !getTime() behaviour: epoch-0 and unparseable both render blank.
        if (!parsed?.getTime()) {
            return "";
        }
        const month = (parsed.getUTCMonth() + 1).toString().padStart(2, "0");
        const day = parsed.getUTCDate().toString().padStart(2, "0");
        return `${month}/${day}/${parsed.getUTCFullYear()}`;
    } else {
        return `${formatDate(date.from)} - ${formatDate(date.to)}`;
    }
}

function formatValue(value?: string | number | Date | PartialDateRange | null): string {
    if (typeof value === "string" || typeof value === "number") {
        return value.toString();
    } else if (value) {
        return formatDate(value);
    } else {
        return "";
    }
}

function IntakeResultsDialog(props: IntakeResultsModalProps & ReduxStateProps & ReduxDispatchProps) {
    const { requestAnalysis, show } = props;
    const data = props.requestAnalysis?.data;

    const dialogParent = useRef<HTMLDivElement>(null);
    let dragX = useRef<number>(0);
    let dragY = useRef<number>(0);

    const [tab, setTab] = useState<Tab>("Details");

    const fields: readonly IntakeResultItem[] = [
        { name: "Request Type", tab: "Details", fieldName: "requestType" },
        { name: "Request Purpose", tab: "Details", fieldName: "requestPurpose" },
        { name: "Patient First Name", tab: "Details", fieldName: "patientGivenName" },
        { name: "Patient Last Name", tab: "Details", fieldName: "patientFamilyName" },
        { name: "Patient Date of Birth", tab: "Details", fieldName: "patientDateOfBirth" },
        { name: "Case Number", tab: "Details", fieldName: "caseNumber" },
        { name: "Clinic Name", tab: "Details", fieldName: "clinicName" },
        { name: "Authorization Date", tab: "Details", fieldName: "authorizationDate" },
        { name: "Request Date", tab: "Details", fieldName: "requestDate" },
        { name: "Received Date", tab: "Details", fieldName: "receivedDate" },
        { name: "Due Date", tab: "Details", fieldName: "dueDate" },
        { name: "Service Dates", tab: "Details", fieldName: "serviceDates", feedbackSupported: false },
        { name: "Requestor Name", tab: "Requestor", fieldName: "requestorName" },
        { name: "Requestor Address 1", tab: "Requestor", fieldName: "requestorAddress1" },
        { name: "Requestor Address 2", tab: "Requestor", fieldName: "requestorAddress2" },
        { name: "Requestor City", tab: "Requestor", fieldName: "requestorCity" },
        { name: "Requestor State", tab: "Requestor", fieldName: "requestorState" },
        { name: "Requestor ZIP Code", tab: "Requestor", fieldName: "requestorZipCode" },
        { name: "Contact First Name", tab: "Requestor", fieldName: "requestorContactGivenName" },
        { name: "Contact Last Name", tab: "Requestor", fieldName: "requestorContactFamilyName" },
        { name: "Contact Fax Number", tab: "Requestor", fieldName: "requestorContactFax" },
        { name: "Contact Phone Number", tab: "Requestor", fieldName: "requestorContactPhone" },
        { name: "Contact Email", tab: "Requestor", fieldName: "requestorContactEmail" },
        { name: "Site Code", tab: "Requestor", fieldName: "requestorSiteCode" },
        { name: "Release Sections", tab: "Details", fieldName: "infoReleaseSections" },
        { name: "Sensitive Information", tab: "Details", fieldName: "sensitiveInfoDeclarations" },
    ];

    // React Bootstrap lets you assign event handlers and refs to its elements,
    // but they don't actually work. There are also strange timing issues with
    // when the header enters the DOM, so refs on other child elements won't
    // work either.
    // This was the only valid workaround I could come up with.
    useEffect(() => {
        const dialogHeader = dialogParent.current?.querySelector(".toast-header") as HTMLDivElement;

        dialogHeader?.addEventListener("mousedown", event => {
            const originalCursorStyle = dialogHeader.style.cursor;
            dialogHeader.style.cursor = "grabbing";
            dragX.current = event.clientX;
            dragY.current = event.clientY;

            document.onmousemove = event => {
                const x = event.clientX - dragX.current;
                const y = event.clientY - dragY.current;
                const newLeft = parseInt(dialogParent.current!.style.left) + x;
                const newTop = parseInt(dialogParent.current!.style.top) + y;
                dragX.current = event.clientX;
                dragY.current = event.clientY;
                dialogParent.current!.style.left = newLeft + "px";
                dialogParent.current!.style.top = newTop + "px";
            };

            document.onmouseup = _event => {
                document.onmouseup = null;
                document.onmousemove = null;
                dialogHeader.style.cursor = originalCursorStyle;
            };
        });
    }, [show]);

    function tabButton(tabName: Tab) {
        return <button
            onClick={() => setTab(tabName)}
            className={tabName === tab ? "active" : ""}
        >
            {tabName}
        </button>
    }

    function onChangeFinding(feedbackItem: keyof RequestAnalysisData, feedbackState: boolean | null) {
        props.setFeedback({
            facilityId: props.facilityId,
            requestBufferId: props.requestBufferId,
            feedbackItem,
            feedback: feedbackState,
        });
    }

    return <div
        id="results-dialog-parent"
        style={{
            left: "0px",
            top: "200px",
            width: "500px",
            height: "calc(100vh - 210px)",
        }}
        ref={dialogParent}
    >
        <Toast
            id="results-dialog"
            show={props.show}
            onClose={props.onClose}
        >
            <Toast.Header closeButton>
                <img className="mr-2" src={InfoFilledSmall} alt="" draggable="false" />
                <span>Intake Results</span>
            </Toast.Header>
            <div className="tab-buttons">
                {tabButton("Details")}
                {tabButton("Requestor")}
                {tabButton("Billing")}
            </div>
            <Toast.Body>
                <table>
                    <tbody>
                        {fields.filter(x => x.tab === tab).map(x => {
                            const value = data?.[x.fieldName];
                            const anything = Boolean(value &&
                                    (typeof value === "string" ||
                                    typeof value === "number" ||
                                    (Array.isArray(value) && value.length) ||
                                    value instanceof Date));
                            const rejected = requestAnalysis?.dataApprovals?.[x.fieldName] === false;
                            const loading = props.intakeFeedbackSubmitStatus[x.fieldName] === API_FETCHING;
                            const feedbackSupported = x.feedbackSupported !== false;

                            return <tr className={rejected ? "rejected" : ""}>
                                <th>
                                    {x.name}
                                </th>
                                <td>
                                    {anything && <>
                                        {Array.isArray(value) ?
                                            <ul>
                                                {value.map((y, i) => <li key={i}>
                                                    {formatValue(y)}
                                                </li>)}
                                            </ul>
                                            :
                                            // TypeScript does not narrow arrays well.
                                            // It still thinks this could be an array.
                                            formatValue(value as any)
                                        }
                                        {!rejected && <CopyToClipboard
                                            copyText={Array.isArray(value) ?
                                                value.map(formatValue).join(", ") :
                                                formatValue(value as any)}
                                            onClick={feedbackSupported ? () => onChangeFinding(x.fieldName, true) : undefined}
                                        />}
                                    </>}
                                </td>
                                <td className="action-col">
                                    {anything && feedbackSupported && (loading ?
                                        <span
                                            className="spinner-border"
                                            style={{width: "13px", height: "13px"}}
                                        ></span>
                                        :
                                        <button
                                            onClick={() => onChangeFinding(x.fieldName, rejected ? null : false)}
                                            title={rejected ? "Undo" : "Reject"}
                                        >
                                            <img
                                                src={rejected ? UndoFilledSmall : CancelFilledSmall}
                                                alt=""
                                                width={13}
                                                height={13}
                                                draggable="false"
                                            />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        })}
                    </tbody>
                </table>
            </Toast.Body>
        </Toast>
    </div>
}

function mapStateToProps(state: RootState): ReduxStateProps {
    return {
        intakeFeedbackSubmitStatus: state.dataEntryState.intakeFeedbackSubmitStatus,
    }
}

function mapDispatchToProps(dispatch: Dispatch): ReduxDispatchProps {
    return {
        setFeedback: (feedback: IntakeFeedback) => dispatch(setFeedbackForIntakeFieldActionCreator(feedback)),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(IntakeResultsDialog);
